import { useMemo } from "react";

import { useChartStore } from "@/stores/chartStore";
import {
  CHART_TYPES,
  TIMEFRAMES,
  type ChartTypeValue,
  type IndicatorCategory,
} from "@/lib/lytick/chart-config";
import {
  INDICATOR_CATALOG,
  getCatalogEntry,
  type IndicatorCatalogEntry,
} from "@/lib/lytick/indicatorRegistry";
import type { IndicatorInstance } from "@/types/indicator";

const CATEGORY_ORDER: IndicatorCategory[] = [
  "trend",
  "momentum",
  "volatility",
];

const CATEGORY_LABELS: Record<IndicatorCategory, string> = {
  trend: "Trend",
  momentum: "Momentum",
  volatility: "Volatility",
};

function normalizeChartType(value: string): ChartTypeValue {
  switch (value) {
    case "candle_solid":
    case "candle_stroke":
    case "candle_heikin_ashi":
    case "area":
    case "ohlc":
      return value;
    case "line":
      return "area";
    case "bar":
      return "ohlc";
    default:
      return "candle_solid";
  }
}

export function useChartSettings() {
  const {
    chartType,
    timeframe,
    indicators,
    setChartType: setChartStoreType,
    setTimeframe: setChartStoreTimeframe,
    addIndicator,
    removeIndicator,
    updateIndicator,
  } = useChartStore();

  const activeChartType = normalizeChartType(chartType);
  const activeTimeframe = timeframe;
  const activeIndicatorCount = indicators.length;

  const groupedIndicators = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        id: category,
        label: CATEGORY_LABELS[category],
        items: INDICATOR_CATALOG.filter((e) => e.category === category),
      })),
    [],
  );

  const setChartType = (nextType: ChartTypeValue) => {
    setChartStoreType(nextType);
  };

  const setTimeframe = (nextTimeframe: string) => {
    setChartStoreTimeframe(nextTimeframe);
  };

  const addIndicatorFromCatalog = (entry: IndicatorCatalogEntry): IndicatorInstance => {
    const instance: IndicatorInstance = {
      id: crypto.randomUUID(),
      name: entry.name,
      paneId: entry.isOverlay ? "candle_pane" : "",
      isOverlay: entry.isOverlay,
      calcParams: [...entry.defaultCalcParams],
      source: entry.defaultSource,
      figureStyles: Object.fromEntries(
        Object.entries(entry.defaultFigureStyles).map(([key, style]) => [key, { ...style }]),
      ),
      visible: true,
    };
    addIndicator(instance);
    return instance;
  };

  const removeIndicatorById = (id: string) => {
    removeIndicator(id);
  };

  const updateIndicatorById = (id: string, patch: Partial<IndicatorInstance>) => {
    updateIndicator(id, patch);
  };

  return {
    activeChartType,
    activeIndicatorCount,
    activeTimeframe,
    chartTypeOptions: CHART_TYPES,
    groupedIndicators,
    indicatorCatalog: INDICATOR_CATALOG,
    indicators,
    timeframeOptions: TIMEFRAMES,
    setChartType,
    setTimeframe,
    addIndicator,
    removeIndicator,
    addIndicatorFromCatalog,
    removeIndicatorById,
    updateIndicatorById,
  };
}
